/**
 * Laptop inventory as a tree (release 2.1 item 14):
 * fleet > business unit > laptop > folder > file, with totals at every level.
 *
 * Children load only when a row opens, 50 at a time with a server-side keyset
 * ("Load 50 more of 1,240"). Only opened rows exist in the browser, and the
 * rows drawn are windowed by the shared VirtualList, so a business unit with
 * thousands of laptops never renders more than a screenful. Filters (one data
 * type, a minimum severity, open only) are applied on the server, so every
 * total shown is the filtered total.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  Laptop,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { ErrorState, LoadingState } from '@foctta/ui-shell';

import { GlassCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { VirtualList } from '@/components/ui/virtual-list';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { cn } from '@/lib/cn';
import {
  getFleetHeatmap,
  getInventoryTree,
  type Freshness,
  type TreeItem,
  type TreeQuery,
  type TreeTotals,
} from '@/lib/fleet-api';

import { FreshnessNote } from './FreshnessNote';
import { LIVENESS_LABEL, SEVERITY_DOT, SEVERITY_TEXT, ago, fmtCount, sharedLabelNote } from './fleet-format';
import { flatten, withPage, type ChildState, type Row } from './tree-model';

const ROOT = 'fleet';
const ROW_HEIGHT = 44;
const PAGE = 50;
type MinSeverity = NonNullable<TreeQuery['minSeverity']>;

interface Filters {
  dataType: string;
  minSeverity: '' | MinSeverity;
  onlyOpen: boolean;
}

function readFilters(): Filters {
  try {
    const q = new URLSearchParams(window.location.search);
    const sev = q.get('minSeverity');
    return {
      dataType: q.get('dataType') ?? '',
      minSeverity: sev && ['critical', 'high', 'medium', 'low', 'info'].includes(sev) ? (sev as MinSeverity) : '',
      onlyOpen: q.get('onlyOpen') !== 'false',
    };
  } catch {
    return { dataType: '', minSeverity: '', onlyOpen: true };
  }
}

/** Keep the filters in the URL so a filtered view can be shared and reloaded. */
function writeFilters(f: Filters): void {
  try {
    const q = new URLSearchParams(window.location.search);
    for (const [k, v] of [
      ['dataType', f.dataType],
      ['minSeverity', f.minSeverity],
      ['onlyOpen', f.onlyOpen ? '' : 'false'],
    ] as const) {
      if (v) q.set(k, v);
      else q.delete(k);
    }
    const s = q.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${s ? `?${s}` : ''}`);
  } catch {
    /* history may be unavailable in an embedded frame */
  }
}

const KIND_ICON = { bu: Building2, device: Laptop, folder: Folder, file: FileText } as const;

function Totals({ t }: { t: TreeTotals }) {
  return (
    <>
      <span className="w-24 text-right tabular-nums text-xs">{fmtCount(t.openFindings)}</span>
      <span className="w-20 text-right tabular-nums text-xs text-muted-foreground">{fmtCount(t.openFiles)}</span>
      <span className="w-24 pl-3 text-xs">
        {t.maxSeverity ? (
          <span className={cn('inline-flex items-center gap-1', SEVERITY_TEXT[t.maxSeverity])}>
            <span className={cn('h-2 w-2 rounded-full', SEVERITY_DOT[t.maxSeverity])} />
            {t.maxSeverity}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </span>
    </>
  );
}

function detailOf(item: TreeItem): string {
  if (item.kind === 'bu') return item.totals.devices !== undefined ? `${fmtCount(item.totals.devices)} laptops with findings` : '';
  if (item.kind === 'device') {
    const parts: string[] = [];
    if (item.pod) {
      parts.push(`${item.pod.liveness ? LIVENESS_LABEL[item.pod.liveness] : 'No check-in'}, checked in ${ago(item.pod.lastHeartbeatAt)}`);
      if (item.pod.hostname && item.pod.hostname !== item.label) parts.push(item.pod.hostname);
    } else {
      parts.push('no enrolled laptop with this name');
    }
    if (item.topTypes && item.topTypes.length > 0) parts.push(item.topTypes.join(', '));
    const shared = sharedLabelNote(item.pod?.pods);
    if (shared) parts.push(shared);
    return parts.join(' · ');
  }
  if (item.kind === 'file' && item.file) {
    return [item.file.types.join(', '), `${fmtCount(item.file.totalFindings)} findings in all`, `seen ${ago(item.file.lastSeenAt)}`]
      .filter(Boolean)
      .join(' · ');
  }
  return '';
}

export function LaptopInventory() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(() => readFilters());
  const [children, setChildren] = useState<Map<string, ChildState>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [rootTotals, setRootTotals] = useState<TreeTotals | null>(null);
  const [freshness, setFreshness] = useState<Freshness | null>(null);
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [generation, setGeneration] = useState(0);
  // The data-type box is applied 400 ms after typing stops, not per keystroke.
  const [typeDraft, setTypeDraft] = useState(filters.dataType);
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.dataType === typeDraft.trim() ? f : { ...f, dataType: typeDraft.trim() })), 400);
    return () => clearTimeout(t);
  }, [typeDraft]);

  const query = useCallback(
    (node: string, cursor?: string): TreeQuery => ({
      node,
      onlyOpen: filters.onlyOpen,
      limit: PAGE,
      ...(filters.dataType ? { dataType: filters.dataType } : {}),
      ...(filters.minSeverity ? { minSeverity: filters.minSeverity } : {}),
      ...(cursor ? { cursor } : {}),
    }),
    [filters],
  );

  // Bumped on every reset; a response from an older filter is dropped.
  const epoch = useRef(0);

  const load = useCallback(
    async (node: string, cursor?: string) => {
      const mine = epoch.current;
      setChildren((prev) => {
        const next = new Map(prev);
        const cur = prev.get(node);
        next.set(node, cur ? { ...cur, loading: true, error: null } : { items: [], nextCursor: null, total: 0, loading: true, error: null });
        return next;
      });
      try {
        const page = await getInventoryTree(query(node, cursor));
        if (mine !== epoch.current) return;
        setChildren((prev) => {
          const next = new Map(prev);
          next.set(node, withPage(prev.get(node), page, Boolean(cursor)));
          return next;
        });
        if (node === ROOT && page.node.totals) setRootTotals(page.node.totals);
        setFreshness(page.freshness);
      } catch (e) {
        if (mine !== epoch.current) return;
        const message = e instanceof Error ? e.message : 'Could not load this level';
        setChildren((prev) => {
          const next = new Map(prev);
          const cur = prev.get(node);
          next.set(node, { ...(cur ?? { items: [], nextCursor: null, total: 0 }), loading: false, error: message });
          return next;
        });
      }
    },
    [query],
  );

  // A filter change starts over: every total on screen must be the filtered one.
  useEffect(() => {
    writeFilters(filters);
    epoch.current += 1;
    setChildren(new Map());
    setExpanded(new Set());
    setRootTotals(null);
    void load(ROOT);
  }, [filters, generation, load]);

  useEffect(() => {
    void getFleetHeatmap({ metric: 'files' })
      .then((h) => setTypeOptions(h.columns.filter((c) => c.count === undefined).map((c) => c.dataType)))
      .catch(() => setTypeOptions([]));
  }, []);

  const toggle = (item: TreeItem): void => {
    if (item.kind === 'file') {
      try {
        sessionStorage.setItem('discovery.findingsSearch', item.file?.ref ?? item.label);
      } catch {
        /* sessionStorage may be unavailable */
      }
      void navigate({ to: '/dspm/findings' });
      return;
    }
    if (!item.hasChildren) return;
    const opening = !expanded.has(item.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (opening) next.add(item.id);
      else next.delete(item.id);
      return next;
    });
    if (opening && !children.has(item.id)) void load(item.id);
  };

  const rows = useMemo(() => flatten(ROOT, children, expanded), [children, expanded]);
  const root = children.get(ROOT);

  const renderRow = (row: Row) => {
    const indent = { paddingLeft: 12 + row.depth * 20 };
    if (row.kind === 'item') {
      const it = row.item;
      const Icon = KIND_ICON[it.kind];
      return (
        <div
          role="treeitem"
          aria-expanded={it.hasChildren ? row.expanded : undefined}
          aria-level={row.depth + 1}
          tabIndex={0}
          onClick={() => toggle(it)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle(it);
            }
          }}
          className="flex h-full items-center border-b border-border/20 pr-3 hover:bg-muted/40 cursor-pointer focus:outline-none focus:bg-muted/50"
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5" style={indent}>
            <span className="w-4 shrink-0 text-muted-foreground">
              {it.hasChildren && it.kind !== 'file' ? (
                row.expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
              ) : null}
            </span>
            <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
            <div className="min-w-0">
              <div className="truncate text-xs font-medium">{it.label}</div>
              <div className="truncate text-[10px] text-muted-foreground">{detailOf(it)}</div>
            </div>
          </div>
          <span className="w-16 text-right tabular-nums text-xs text-muted-foreground">
            {it.kind === 'device' && it.riskScore !== undefined ? it.riskScore.toFixed(1) : ''}
          </span>
          <Totals t={it.totals} />
        </div>
      );
    }
    if (row.kind === 'more') {
      return (
        <div className="flex h-full items-center border-b border-border/20" style={indent}>
          <Button
            size="sm"
            variant="glass"
            disabled={row.loading}
            onClick={() => {
              const cur = children.get(row.parentId);
              if (cur?.nextCursor) void load(row.parentId, cur.nextCursor);
            }}
          >
            {row.loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Load {PAGE} more (showing {fmtCount(row.shown)} of {fmtCount(row.total)})
          </Button>
        </div>
      );
    }
    if (row.kind === 'loading') {
      return (
        <div className="flex h-full items-center gap-2 border-b border-border/20 text-xs text-muted-foreground" style={indent}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
        </div>
      );
    }
    if (row.kind === 'error') {
      return (
        <div className="flex h-full items-center gap-2 border-b border-border/20 text-xs text-destructive" style={indent}>
          <AlertTriangle className="h-3.5 w-3.5" /> {row.message}
          <Button size="sm" variant="ghost" onClick={() => void load(row.parentId)}>
            Try again
          </Button>
        </div>
      );
    }
    return (
      <div className="flex h-full items-center border-b border-border/20 text-xs text-muted-foreground" style={indent}>
        Nothing here matches the filters.
      </div>
    );
  };

  const filtered = Boolean(filters.dataType || filters.minSeverity);

  return (
    <PageContainer>
      <PageHeader
        title="Endpoint Data"
        subtitle="Sensitive files the endpoint agent found on laptops: business unit, laptop, folder, file. Open a row to load what is inside it; totals are counted on the server for every level. Databases and cloud sources are in Data Catalog; agent status is in Endpoint Agents."
        actions={
          <Button size="sm" variant="glass" onClick={() => setGeneration((g) => g + 1)}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        }
      />

      <GlassCard className="p-4 mb-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Data type
            <input
              list="laptop-inventory-types"
              value={typeDraft}
              onChange={(e) => setTypeDraft(e.target.value)}
              placeholder="Any data type"
              className="mt-1 block w-48 glass-lite rounded px-2 py-1 text-xs text-foreground bg-transparent outline-none"
            />
            <datalist id="laptop-inventory-types">
              {typeOptions.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </label>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Severity
            <select
              value={filters.minSeverity}
              onChange={(e) => setFilters((f) => ({ ...f, minSeverity: e.target.value as Filters['minSeverity'] }))}
              className="mt-1 block glass-lite rounded px-2 py-1 text-xs text-foreground bg-background/40 border border-border/40"
            >
              <option value="">Any severity</option>
              <option value="critical">Critical only</option>
              <option value="high">High and above</option>
              <option value="medium">Medium and above</option>
              <option value="low">Low and above</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground pb-1">
            <input
              type="checkbox"
              checked={filters.onlyOpen}
              onChange={(e) => setFilters((f) => ({ ...f, onlyOpen: e.target.checked }))}
            />
            Only rows with open findings
          </label>
          {filtered && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setTypeDraft('');
                setFilters({ dataType: '', minSeverity: '', onlyOpen: true });
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
        <FreshnessNote freshness={freshness} className="mt-3" />
      </GlassCard>

      {root?.error && root.items.length === 0 && (
        <ErrorState title="Could not load endpoint data" detail={root.error} onRetry={() => void load(ROOT)} />
      )}
      {!root?.error && (!root || (root.loading && root.items.length === 0)) && <LoadingState caption="Adding up the fleet..." />}

      {root && !root.loading && !root.error && root.items.length === 0 && (
        <GlassCard className="p-8 text-center">
          <Laptop className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm font-medium">{filtered ? 'Nothing matches these filters' : 'No laptop findings yet'}</div>
          <p className="mt-1 text-xs text-muted-foreground">
            {filtered
              ? 'Try another data type or a lower severity.'
              : 'Files with sensitive data appear here once laptops have scanned and reported.'}
          </p>
        </GlassCard>
      )}

      {root && root.items.length > 0 && (
        <GlassCard className="p-0 overflow-hidden" role="tree" aria-label="Endpoint Data">
          <div className="flex items-center border-b border-border/40 bg-muted/30 pr-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="flex-1 pl-3">
              Fleet
              {rootTotals ? ` · ${fmtCount(rootTotals.devices ?? 0)} laptops with findings` : ''}
              {filtered ? ' · filtered' : ''}
            </span>
            <span className="w-16 text-right">Risk</span>
            <span className="w-24 text-right">Open findings</span>
            <span className="w-20 text-right">Files</span>
            <span className="w-24 pl-3">Worst</span>
          </div>
          {rootTotals && (
            <div className="flex items-center border-b border-border/40 pr-3 py-2 text-xs font-medium">
              <span className="flex-1 pl-3">All business units you can see</span>
              <span className="w-16" />
              <Totals t={rootTotals} />
            </div>
          )}
          <VirtualList<Row>
            items={rows}
            height={Math.min(640, Math.max(ROW_HEIGHT * 3, rows.length * ROW_HEIGHT))}
            itemHeight={ROW_HEIGHT}
            getKey={(r) => r.key}
            renderRow={(r) => renderRow(r)}
          />
        </GlassCard>
      )}
    </PageContainer>
  );
}
